import React, { useState } from 'react';
import { Alert, Button, Popover, Progress } from 'antd';
import { CheckCircleFilled, StarFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TGraphicDesignLessonConfig, TGDRuleType, TGDTier } from '../../../models/courses/graphicDesign';
import { TGDEvaluation } from '../core/engine';
import { pickI18n } from '../core/i18n';

interface GDMissionPanelProps {
  config: TGraphicDesignLessonConfig;
  evaluation: TGDEvaluation;
  displayedStars: 0 | 1 | 2 | 3;
  hintsUsed: number;
  attempts: number;
  nextHint?: () => TGraphicDesignLessonConfig['hints'][number] | null;
  teacher?: boolean;
}

const COUNT_RULES: TGDRuleType[] = ['elementCountMax', 'maxColors', 'minColors'];

const GDMissionPanel: React.FC<GDMissionPanelProps> = ({ config, evaluation, displayedStars,
  hintsUsed, attempts, nextHint, teacher = false }) => {
  const { t, i18n } = useTranslation();
  const [hint, setHint] = useState('');
  const [hintOpen, setHintOpen] = useState(false);
  const lang = i18n.language;
  const showFailures = teacher || attempts > 0 || hintsUsed > 0;
  const ruleGroup = (tier: TGDTier, title?: string) => config.rules.some(rule =>
    rule.tier === tier) && <section>
    {title && <h4>{title}</h4>}
    {config.rules.filter(rule => rule.tier === tier).map(rule => {
      const result = evaluation.results.find(item => item.ruleId === rule.id);
      const raw = result?.passed ?? false;
      // Les regles de comptage affichent la valeur de l'eleve : "8 elements maximum" seul
      // se lit mal ; "tu en as 3" rend la contrainte concrete.
      const counted = COUNT_RULES.includes(rule.type) && result?.measured !== undefined
        && /^\d+$/.test(result.measured);
      // Style et bonus sont les etoiles 2 et 3 : ils ne se gagnent qu'apres les objectifs du
      // client. Avant, une contrainte "vraie" sur un canvas vide (0 element <= 8) ou un chrono
      // encore sous la limite apparaitraient coches sans que l'eleve ait rien fait.
      const passed = raw && (teacher || tier === 'required' || evaluation.requiredMet);
      return <div key={rule.id} style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'start' }}>
          {passed ? <CheckCircleFilled style={{ color: '#0e9f6e' }} aria-label={t(
            'graphicDesignLesson.student.passed')} />
            : <span aria-label={t('graphicDesignLesson.student.pending')}>○</span>}
          <span>{pickI18n(rule.label, lang)}
            {counted && <small>{' · '}{t('graphicDesignLesson.student.measured',
              { value: result?.measured })}</small>}</span>
        </div>
        {!passed && showFailures && rule.failMessage && <small style={{ color: '#9f3030' }}>
          {pickI18n(rule.failMessage, lang)}
        </small>}
      </div>;
    })}
  </section>;
  // Etoile plafonnee (indices ou premier essai faux) : on dit pourquoi, au lieu de
  // pousser l'eleve vers des bonus qui ne changeront plus rien.
  const capped = evaluation.stars > displayedStars;
  const guidance = capped ? t(hintsUsed >= 3 ? 'graphicDesignLesson.student.capHints'
    : 'graphicDesignLesson.student.capFirstTry')
    : displayedStars === 0 ? t('graphicDesignLesson.student.requiredMissing')
    : displayedStars === 1 ? t('graphicDesignLesson.student.constraintsMissing')
      : displayedStars === 2 ? t('graphicDesignLesson.student.bonusMissing')
        : t('graphicDesignLesson.student.allStars');
  return <div className="gd-panel">
    <section>
      <h3>{t('graphicDesignLesson.student.brief')}</h3>
      <p>{pickI18n(config.brief, lang)}</p>
      {config.clientName && <small>— {config.clientName}</small>}
    </section>
    <section>
      <h3>{t('graphicDesignLesson.student.objectives')}</h3>
      <p>{evaluation.requiredPassed} / {evaluation.requiredTotal}
        {evaluation.requiredThreshold < evaluation.requiredTotal && <small>
          {' · '}{t('graphicDesignLesson.student.enoughToWin',
            { count: evaluation.requiredThreshold })}</small>}</p>
      <Progress percent={evaluation.requiredThreshold ? Math.min(100,
        evaluation.requiredPassed / evaluation.requiredThreshold * 100) : 100}
        showInfo={false} />
      {ruleGroup('required')}
    </section>
    {ruleGroup('constraint', t('graphicDesignLesson.student.style'))}
    {ruleGroup('bonus', t('graphicDesignLesson.student.bonus'))}
    <section>
      <div aria-label={t('graphicDesignLesson.student.stars', { count: displayedStars })}>
        {[1, 2, 3].map(star => <StarFilled key={star} style={{ fontSize: 23, marginRight: 6,
          color: star <= displayedStars ? '#b77800' : '#aeb8c2' }} />)}
      </div>
      <p>{t('graphicDesignLesson.student.nextStarHint', { hint: guidance })}</p>
    </section>
    {teacher ? <section>
      <h4>{t('graphicDesignLesson.student.hints')}</h4>
      {config.hints.map((item, index) => <p key={index}>
        {index + 1}. {pickI18n(item, lang)}
      </p>)}
    </section> : config.hints.length > 0 && <Popover open={hintOpen}
      onOpenChange={setHintOpen} trigger="click"
      content={<Alert type="info" showIcon message={hint} />}>
      <Button disabled={hintsUsed >= config.hints.length} onClick={() => {
        const next = nextHint?.();
        if (next) { setHint(pickI18n(next, lang)); setHintOpen(true); }
      }}>{hintsUsed >= config.hints.length
          ? t('graphicDesignLesson.student.hintsDone', { total: config.hints.length })
          : t('graphicDesignLesson.student.hint', { n: hintsUsed + 1,
            total: config.hints.length })}</Button>
    </Popover>}
  </div>;
};

export default GDMissionPanel;
